import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { formatUSD, daysBetween, formatDate } from "../lib/format";
import ReportPhoto from "../components/ReportPhoto";
import QuillaIA from "../components/QuillaIA";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { CaretRight, Wrench, Package, Users, CheckCircle, Sparkle, ArrowsCounterClockwise } from "@phosphor-icons/react";

const SEV_ORDER = { grave: 0, media: 1, leve: 2 };

export default function Maintenance() {
  const { session } = useAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [route, setRoute] = useState("interna");
  const [cost, setCost] = useState(0);
  const [supplierId, setSupplierId] = useState("");
  const [techNote, setTechNote] = useState("");
  const [assetMemory, setAssetMemory] = useState(null);
  const [copilotReport, setCopilotReport] = useState(null);
  const isProtected = session?.hotel?.license_status === "PROTEGIDA";

  const load = () => {
    api.get("/reports").then(r => setReports(r.data));
    api.get("/suppliers").then(r => setSuppliers(r.data));
    api.get("/assets").then(r => setAssets(r.data));
  };
  useEffect(() => { load(); }, []);

  const pending = reports
    .filter(r => r.status === "REPORTADO")
    .sort((a,b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity]);
  const inWork = reports.filter(r => ["EN_MANTENIMIENTO", "ESPERANDO_REPUESTO", "CONTRATISTA"].includes(r.status));

  const openValidate = (r) => {
    setSelected(r); setRoute("interna");
    const guess = assets.find(a => a.name === r.asset_hint);
    setCost(guess?.repair_price || 0);
    setSupplierId(guess?.default_supplier_id || "");
    setTechNote("");
    // Carga la memoria del activo (historial + resumen IA + reincidencia)
    setAssetMemory(null);
    api.get(`/ai/asset-memory/${r.id}`).then(res => setAssetMemory(res.data)).catch(()=>{});
  };

  const validate = async () => {
    if (isProtected) return;
    try {
      await api.post(`/reports/${selected.id}/validate`, {
        route, estimated_cost: parseFloat(cost) || 0,
        supplier_id: supplierId || null, tech_note: techNote,
      });
      toast.success("Reporte validado y enviado a gerencia");
      setSelected(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const partReceived = async (id) => {
    await api.post(`/reports/${id}/part-received`); toast.success("Repuesto recibido"); load();
  };
  const close = async (id) => {
    const { data } = await api.post(`/reports/${id}/close`);
    toast.success(`Cerrado · Health Index ${data.health_index.score}/100`); load();
  };

  const daysSince = (iso) => daysBetween(iso);

  return (
    <AppShell title="Cola de mantenimiento">
      {isProtected && <div className="q-card p-3 mb-4 text-sm text-[color:var(--q-muted)]">Solo lectura · licencia PROTEGIDA</div>}

      {/* Pending validation */}
      <section className="mb-8">
        <div className="q-label mb-3">Nuevos reportes · a validar ({pending.length})</div>
        <div className="grid gap-3">
          {pending.length === 0 && <div className="q-card p-6 text-center text-[color:var(--q-muted)]">Nada pendiente. Todo al día.</div>}
          {pending.map(r => (
            <div key={r.id} data-testid={`pending-${r.room}`} onClick={() => nav(`/report/${r.id}`)}
                 className="q-card p-4 flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-3 sm:gap-4 cursor-pointer" style={{borderLeft: `4px solid ${r.severity === "grave" ? "#ef4444" : r.severity === "media" ? "#f59e0b" : "#3b82f6"}`}}>
              <div className="text-2xl font-mono font-light break-words min-w-0 basis-full sm:basis-auto sm:min-w-[3rem] sm:max-w-[12rem]">{r.room}</div>
              <div className="flex-1 min-w-0 basis-full sm:basis-auto order-3 sm:order-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`chip chip-${r.severity} shrink-0`}>{r.severity.toUpperCase()}</span>
                  <span className="text-xs text-[color:var(--q-muted)] truncate">{r.reporter_name}</span>
                </div>
                <div className="text-sm mt-1 break-words">{r.note}</div>
              </div>
              <button data-testid={`validate-btn-${r.room}`} disabled={isProtected}
                      onClick={(e) => { e.stopPropagation(); openValidate(r); }}
                      className="q-btn-primary flex items-center gap-2 disabled:opacity-40 shrink-0 ml-auto sm:ml-0 order-2 sm:order-3">
                Validar <CaretRight size={14}/>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* In progress */}
      <section>
        <div className="q-label mb-3">En ejecución / esperando ({inWork.length})</div>
        <div className="grid gap-3">
          {inWork.map(r => (
            <div key={r.id} data-testid={`inwork-${r.room}`} onClick={() => nav(`/report/${r.id}`)}
                 className="q-card p-4 flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-3 sm:gap-4 cursor-pointer">
              <div className="text-2xl font-mono font-light break-words min-w-0 basis-full sm:basis-auto sm:min-w-[3rem] sm:max-w-[12rem]">{r.room}</div>
              <div className="flex-1 min-w-0 basis-full sm:basis-auto order-3 sm:order-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`chip chip-${r.severity} shrink-0`}>{r.severity}</span>
                  <span className={`badge-route badge-${r.route} shrink-0`}>{r.route}</span>
                  {r.status === "ESPERANDO_REPUESTO" && (
                    <span className="text-xs font-mono text-[color:var(--q-gold-dark)] q-pulse whitespace-nowrap">
                      Esperando repuesto · {daysSince(r.purchase_confirmed_at)}d
                    </span>
                  )}
                </div>
                <div className="text-sm mt-1 break-words">{r.note}</div>
              </div>
              <div className="flex flex-col gap-2 shrink-0 ml-auto sm:ml-0 order-2 sm:order-3" onClick={(e) => e.stopPropagation()}>
                {r.status === "ESPERANDO_REPUESTO" && (
                  <button data-testid={`part-received-${r.room}`} disabled={isProtected} onClick={() => partReceived(r.id)} className="q-btn-gold text-xs !py-2 disabled:opacity-40">
                    <Package size={14} className="inline mr-1"/> Repuesto recibido
                  </button>
                )}
                {r.status === "EN_MANTENIMIENTO" && (
                  <button data-testid={`close-btn-${r.room}`} disabled={isProtected} onClick={() => close(r.id)} className="q-btn-primary text-xs !py-2 disabled:opacity-40">
                    <CheckCircle size={14} className="inline mr-1"/> Cerrar
                  </button>
                )}
                {r.status === "CONTRATISTA" && (
                  <button data-testid={`close-btn-${r.room}`} disabled={isProtected} onClick={() => close(r.id)} className="q-btn-ghost text-xs !py-2 disabled:opacity-40">
                    <Users size={14} className="inline mr-1"/> Cerrar tras contratista
                  </button>
                )}
              </div>
            </div>
          ))}
          {inWork.length === 0 && <div className="q-card p-6 text-center text-[color:var(--q-muted)]">Sin trabajos activos.</div>}
        </div>
      </section>

      {/* Validate modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-[#132340]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="q-card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="q-label text-[color:var(--q-muted)] mb-1">Validar reporte · hab. {selected.room}</div>
            <h3 className="text-xl mb-4">{selected.note}</h3>

            {selected.photo_paths?.length > 0 && (
              <div className="mb-4 grid grid-cols-2 gap-2">
                {selected.photo_paths.map(p => (
                  <ReportPhoto key={p} path={p} className="w-full h-32" />
                ))}
              </div>
            )}

            {assetMemory && (assetMemory.history?.length > 1 || assetMemory.summary) && (
              <div className="mb-4 p-3 rounded-lg border border-[color:var(--q-gold)]/40 bg-[color:var(--q-gold)]/5" data-testid="asset-memory">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkle size={14} weight="fill" className="text-[color:var(--q-gold-dark)]"/>
                  <div className="q-label text-[color:var(--q-gold-dark)]">Memoria del activo · QUILLA IA</div>
                  {assetMemory.reincidence && (
                    <span className="chip chip-grave text-[10px]" data-testid="reincidence-flag">
                      <ArrowsCounterClockwise size={10} className="inline mr-0.5" /> REINCIDENCIA
                    </span>
                  )}
                </div>
                <div className="text-xs text-[color:var(--q-muted)]">
                  {assetMemory.recent_90d} reportes en 90 días · gastado histórico: <span className="font-mono font-semibold">{formatUSD(assetMemory.total_spent_usd)}</span>
                </div>
                {assetMemory.summary && <div className="text-sm mt-2 leading-relaxed whitespace-pre-wrap">{assetMemory.summary}</div>}
              </div>
            )}

            <button onClick={() => setCopilotReport(selected)} data-testid="open-copilot" className="q-btn-ghost mb-4 w-full flex items-center justify-center gap-2 border-[color:var(--q-navy)] text-[color:var(--q-navy)]">
              <Sparkle size={14} weight="fill"/> Consultar a QUILLA IA
            </button>

            <div className="q-label mb-2">Ruta</div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                {v:"interna", label:"Reparación interna", Ic: Wrench},
                {v:"repuesto", label:"Requiere repuesto", Ic: Package},
                {v:"contratista", label:"Contratista", Ic: Users},
              ].map(o => (
                <button key={o.v} data-testid={`route-${o.v}`} onClick={() => setRoute(o.v)} className={`p-3 rounded-lg border text-left ${route === o.v ? `badge-${o.v}` : "bg-white border-[color:var(--q-border)]"}`}>
                  <o.Ic size={18} weight="fill" />
                  <div className="text-xs font-semibold mt-1">{o.label}</div>
                </button>
              ))}
            </div>

            <div className="q-label mb-2">Costo estimado (USD)</div>
            <input data-testid="cost-input" value={cost} onChange={(e) => setCost(e.target.value)} type="number" className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] mb-4 font-mono" />

            {route !== "interna" && (
              <>
                <div className="q-label mb-2">Proveedor sugerido</div>
                <select data-testid="supplier-select" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] mb-4">
                  <option value="">Sin asignar</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name} · {s.category}</option>)}
                </select>
              </>
            )}

            <div className="q-label mb-2">Nota técnica</div>
            <textarea data-testid="tech-note" value={techNote} onChange={(e) => setTechNote(e.target.value)} className="w-full h-16 p-2 rounded-lg border border-[color:var(--q-border)] text-sm mb-4" />

            <div className="flex gap-2 justify-end">
              <button className="q-btn-ghost" onClick={() => setSelected(null)}>Cancelar</button>
              <button data-testid="validate-submit" className="q-btn-primary" onClick={validate}>Validar y enviar</button>
            </div>
          </div>
        </div>
      )}

      {copilotReport && (
        <QuillaIA context="copilot" reportId={copilotReport.id} onClose={() => setCopilotReport(null)}/>
      )}
    </AppShell>
  );
}
